import { html, render } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Dialog } from '@vaadin/vaadin-core'
import { View } from '../view'
import { DeviceStore, DownloadResult } from '../../stores'

@customElement('kob-device-download')
export class DeviceDownload extends View {

    @property({ type: Object })
    deviceStore: DeviceStore

    result: DownloadResult

    @property()
    dialogOpened: boolean

    @property()
    installedOpened: boolean

    formRoot

    render() {
        return html`
            <vaadin-dialog
                aria-label="Successfully Installed"
                .opened="${this.installedOpened}"
                .renderer="${this.renderSuccess}"
            ></vaadin-dialog>
            <vaadin-dialog
                aria-label="Install Koblime"
                .opened="${this.dialogOpened}"
                @opened-changed="${(e: CustomEvent) => (this.dialogOpened = e.detail.value)}"
                .renderer="${this.renderContent}"
            ></vaadin-dialog>
        `
    }

    handleClose = () => {
        this.deviceStore = null
        this.installedOpened = false
        this.dialogOpened = false
    }

    handleDownload = async () => {
        this.result = null
        this.renderContent(this.formRoot)
        
        const result = await this.deviceStore.installKoblime()
        if (result.status !== 'error' && result.status !== 'cancel') {
            this.dialogOpened = false
            if (this.deviceStore.canDirectInstall)
                this.installedOpened = true
            return
        }

        this.result = result
        this.renderContent(this.formRoot)
    }

    renderSuccess = (root: HTMLElement, dialog?: Dialog) => {
        render(html`
                <vaadin-vertical-layout
                theme="spacing"
                style="width: 400px; max-width: 100%; align-items: stretch;"
                >
                    <h2 style="margin: var(--lumo-space-m) 0 0 0;">
                        Download Completed
                    </h2>
                    <vaadin-vertical-layout style="align-items: stretch;">
                        <p>
                            The Koblime app has been successfully copied to your device. Please safely unmount 
                            and unplug your kobo. 
                        </p>
                        <p>
                            Once unplugged, the Kobo will reboot in order to install the Koblime app.
                        </p>
                    </vaadin-vertical-layout>
                    <vaadin-horizontal-layout theme="spacing" style="justify-content: flex-end">
                        <vaadin-button theme="primary" @click="${this.handleClose}">Done</vaadin-button>
                    </vaadin-horizontal-layout>
                </vaadin-vertical-layout>
        `, root)
    }

    isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    mobileTemplate = () => html`
        <vaadin-vertical-layout
        theme="spacing"
        style="width: 400px; max-width: 100%; align-items: stretch;"
        >
            <h2 style="margin: var(--lumo-space-m) 0 0 0;">
                Install Koblime
            </h2>
            <vaadin-vertical-layout style="align-items: stretch;">
                <p>
                    <span theme="badge error">
                        Koblime requires desktop computer to install to your Kobo
                    </span>
                </p>
            </vaadin-vertical-layout>
            <vaadin-horizontal-layout theme="spacing" style="justify-content: flex-end">
                <vaadin-button @click="${this.handleClose}">Cancel</vaadin-button>
            </vaadin-horizontal-layout>
        </vaadin-vertical-layout>

    `
    
    renderContent = (root: HTMLElement, dialog?: Dialog) => {
        this.formRoot = root

        const template = this.isMobile() ? 
            this.mobileTemplate() :
            html`
                <vaadin-vertical-layout
                theme="spacing"
                style="width: 400px; max-width: 100%; align-items: stretch;"
                >
                    <h2 style="margin: var(--lumo-space-m) 0 0 0;">
                        Install Koblime
                    </h2>
                    <vaadin-vertical-layout style="align-items: stretch;">
                        <p>
                            <span theme="badge error">Do not share this file with anyone else or put it on a different device as it
                                contains account credentials specific to this device!
                            </span>
                        </p>

                        ${this.result ?
                            html`
                                <span theme="badge error">${this.result.message}</span>
                            ` : ''
                        }

                        ${this.deviceStore.canDirectInstall ?
                            html`
                                <p>Your browser supports File System Access.</p>
                                <p>Select your Kobo device and we will install the Koblime app
                                onto the device in the correct location.</p>
                            ` : 
                            html`
                                <p>Your browser does not support File System Access.</p>
                                <p>Download the KoboRoot.tgz and copy it to your Kobo Reader's hidden .kobo
                                folder.</p>`
                        }
                    </vaadin-vertical-layout>
                    <vaadin-horizontal-layout theme="spacing" style="justify-content: flex-end">
                        <vaadin-button @click="${this.handleClose}">Cancel</vaadin-button>
                        <vaadin-button theme="primary" @click="${this.handleDownload}">
                        ${this.deviceStore.canDirectInstall ?
                            'Select Kobo' : 'Download'
                        }
                        </vaadin-button>
                    </vaadin-horizontal-layout>
                </vaadin-vertical-layout>
            `
            
        render(template, root)
    }
}